import type { Tinte } from '@/payload-types'

/**
 * Base-colour families of the paint finder (`/cercatinte?marca=&modello=&colore=`).
 * Tinte have no colour field: the family is derived from the name, then from `hex`.
 * Pure module, shared by the queries and the UI.
 */
export const famiglieColore = [
  { value: 'bianco', label: 'Bianco', swatch: '#f6f5f0' },
  { value: 'nero', label: 'Nero', swatch: '#1c1d1f' },
  { value: 'grigio', label: 'Grigio / argento', swatch: 'linear-gradient(135deg,#7d858b,#d3d7da)' },
  { value: 'rosso', label: 'Rosso', swatch: '#c3242b' },
  { value: 'blu', label: 'Blu', swatch: '#1f4c9c' },
  { value: 'verde', label: 'Verde', swatch: '#2f7a3b' },
  { value: 'giallo', label: 'Giallo / oro', swatch: 'linear-gradient(135deg,#f2c230,#b8892a)' },
  { value: 'arancio', label: 'Arancio', swatch: '#ec6a1e' },
  { value: 'marrone', label: 'Marrone / beige', swatch: 'linear-gradient(135deg,#6e4630,#d8c3a0)' },
  { value: 'viola', label: 'Viola', swatch: '#6b3f99' },
  {
    value: 'altro',
    label: 'Altro',
    swatch: 'conic-gradient(#c3242b,#f2c230,#2f7a3b,#1f4c9c,#6b3f99,#c3242b)',
  },
] as const

export type FamigliaColore = (typeof famiglieColore)[number]['value']

export const isFamigliaColore = (value: unknown): value is FamigliaColore =>
  famiglieColore.some((f) => f.value === value)

export const famigliaLabel = (value: FamigliaColore) =>
  famiglieColore.find((f) => f.value === value)?.label ?? value

/**
 * Colour words, Italian first, plus the French/German/English/Spanish names the
 * manufacturers use (BLANC BANQUISE, ROT, SILVER…). Matched as whole words, accents folded.
 */
const paroleChiave: Record<Exclude<FamigliaColore, 'altro'>, string[]> = {
  bianco: [
    'BIANCO',
    'BIANCA',
    'AVORIO',
    'WHITE',
    'IVORY',
    'BLANC',
    'BLANCHE',
    'BLANCO',
    'WEISS',
    'BRANCO',
  ],
  nero: ['NERO', 'NERA', 'EBANO', 'BLACK', 'EBONY', 'NOIR', 'NOIRE', 'SCHWARZ', 'NEGRO', 'PRETO'],
  grigio: [
    'GRIGIO',
    'GRIGIA',
    'ARGENTO',
    'ARGENTATO',
    'ANTRACITE',
    'TITANIO',
    'PLATINO',
    'ALLUMINIO',
    'ACCIAIO',
    'GRAFITE',
    'PIOMBO',
    'CANNA DI FUCILE',
    'GREY',
    'GRAY',
    'SILVER',
    'STEEL',
    'GRAPHITE',
    'TITANIUM',
    'PLATINUM',
    'ANTHRACITE',
    'GRIS',
    'ARGENT',
    'GRAU',
    'SILBER',
    'PLATA',
  ],
  rosso: [
    'ROSSO',
    'ROSSA',
    'ROSA',
    'AMARANTO',
    'BORDEAUX',
    'BORDO',
    'CARMINIO',
    'RUBINO',
    'CILIEGIA',
    'VINACCIA',
    'RED',
    'RUBY',
    'PINK',
    'ROUGE',
    'ROT',
    'ROJO',
  ],
  blu: [
    'BLU',
    'AZZURRO',
    'AZZURRA',
    'CELESTE',
    'TURCHESE',
    'COBALTO',
    'INDACO',
    'BLUE',
    'NAVY',
    'BLEU',
    'BLAU',
    'AZUL',
  ],
  verde: [
    'VERDE',
    'SMERALDO',
    'OLIVA',
    'MENTA',
    'GREEN',
    'EMERALD',
    'OLIVE',
    'VERT',
    'GRUN',
    'GRUEN',
  ],
  giallo: [
    'GIALLO',
    'GIALLA',
    'ORO',
    'DORATO',
    'OCRA',
    'SENAPE',
    'LIMONE',
    'YELLOW',
    'GOLD',
    'JAUNE',
    'DORE',
    'GELB',
    'AMARILLO',
  ],
  arancio: ['ARANCIO', 'ARANCIONE', 'ARANCIATO', 'MANDARINO', 'ORANGE', 'NARANJA'],
  marrone: [
    'MARRONE',
    'BEIGE',
    'SABBIA',
    'NOCCIOLA',
    'CAFFE',
    'MOKA',
    'CIOCCOLATO',
    'BRONZO',
    'RAME',
    'TABACCO',
    'CAMMELLO',
    'CUOIO',
    'TESTA DI MORO',
    'BROWN',
    'SAND',
    'CHAMPAGNE',
    'BRONZE',
    'COPPER',
    'MOCHA',
    'BRUN',
    'BRAUN',
    'MARRON',
    'KUPFER',
  ],
  viola: [
    'VIOLA',
    'VIOLETTO',
    'LILLA',
    'MALVA',
    'PRUGNA',
    'MELANZANA',
    'PORPORA',
    'FUCSIA',
    'MAGENTA',
    'PURPLE',
    'VIOLET',
    'MAUVE',
    'AUBERGINE',
    'LILAS',
    'LILA',
  ],
}

const parole = Object.entries(paroleChiave).flatMap(([famiglia, words]) =>
  words.map((word) => ({
    famiglia: famiglia as FamigliaColore,
    regex: new RegExp(`(?:^|[^A-Z0-9])${word.replace(/ /g, '\\s+')}(?=$|[^A-Z0-9])`),
  })),
)

/** "Weiß" → "WEISS", "Doré" → "DORE", "Grün" → "GRUN". */
const normalizza = (value: string) =>
  value.replace(/ß/g, 'ss').normalize('NFD').replace(/\p{M}/gu, '').toUpperCase()

/**
 * The family named in a text, or null. When the text names two colours ("GRIGIO VERDE"),
 * the first word wins: in Italian the noun comes first and the rest qualifies it.
 */
export const famigliaDaNome = (testo: string): FamigliaColore | null => {
  const value = normalizza(testo)
  let best: { famiglia: FamigliaColore; index: number } | null = null
  for (const { famiglia, regex } of parole) {
    const match = regex.exec(value)
    if (match && (!best || match.index < best.index)) best = { famiglia, index: match.index }
  }
  return best?.famiglia ?? null
}

/** Every family named in a text: "BLACK LAVA RED" → nero, rosso. */
export const famiglieDaNome = (testo: string): Set<FamigliaColore> => {
  const value = normalizza(testo)
  return new Set(parole.filter(({ regex }) => regex.test(value)).map((p) => p.famiglia))
}

/** `#rrggbb` → hue 0–360, saturation and lightness 0–1. Null for anything else. */
export const hexToHsl = (hex: string) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null

  const n = parseInt(match[1], 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => c / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return { h: 0, s: 0, l }

  const s = d / (1 - Math.abs(2 * l - 1))
  const h = max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4

  return { h: h * 60, s, l }
}

/** Classifies a swatch by lightness first (white/black/grey), then by hue. */
export const famigliaDaHex = (hex: string | null | undefined): FamigliaColore | null => {
  const hsl = hex ? hexToHsl(hex) : null
  if (!hsl) return null

  const { h, s, l } = hsl
  if (l >= 0.92) return 'bianco'
  if (l <= 0.1) return 'nero'
  if (s < 0.15) return l >= 0.85 ? 'bianco' : l <= 0.18 ? 'nero' : 'grigio'

  // Dark or washed-out oranges and yellows read as browns and beiges.
  if (h >= 15 && h < 70 && (l < 0.35 || (s < 0.45 && l > 0.55))) return 'marrone'
  if (h < 15 || h >= 330) return 'rosso'
  if (h < 40) return 'arancio'
  if (h < 70) return 'giallo'
  if (h < 170) return 'verde'
  if (h < 260) return 'blu'
  return 'viola'
}

type TintaColore = Pick<Tinte, 'nome'> & {
  hex?: Tinte['hex']
  nomiAlternativi?: Tinte['nomiAlternativi']
}

/**
 * The base-colour family of a paint: a colour word in `nome`, then in `nomiAlternativi`,
 * then the hue of `hex`. `altro` when nothing tells.
 */
export const famigliaColore = (tinta: TintaColore): FamigliaColore => {
  for (const testo of [tinta.nome, ...(tinta.nomiAlternativi ?? []).map((n) => n.valore)]) {
    const famiglia = testo ? famigliaDaNome(testo) : null
    if (famiglia) return famiglia
  }
  return famigliaDaHex(tinta.hex) ?? 'altro'
}
